/**
 * Layer 0 — terrain preprocessing (SPEC §4).
 *
 * Split in two: `analyseTerrain` computes everything that depends only on the
 * height grid (once per DEM load), `windMasks` computes the wind-relative
 * quantities (once per wind direction change).
 */

import type { HeightGrid, TerrainStatic, TerrainWindMasks } from './types';

/** Roughness length by land-cover class, metres (SPEC §3.2). */
export const Z0_BY_COVER: Record<string, number> = {
  water: 0.0002,
  bare: 0.005,
  grass: 0.03,
  crops: 0.05,
  scrub: 0.1,
  forest: 0.8,
  urban: 1.0,
  snow: 0.001,
};

const clampIdx = (v: number, hi: number) => (v < 0 ? 0 : v > hi ? hi : v);

/**
 * Aerodynamic roughness length of open water, metres (SPEC §3.2).
 *
 * Three orders of magnitude below any land surface — which is exactly why the
 * sea has to be identified rather than left as whatever the DEM says.
 */
export const SEA_Z0 = Z0_BY_COVER.water;

/**
 * Central-difference gradients with edge clamping, plus second derivatives for
 * curvature. Curvature uses the standard Zevenbergen–Thorne quadratic surface
 * coefficients, which are far less noisy on real DEMs than raw finite
 * differences of the gradient.
 */
export function analyseTerrain(grid: HeightGrid, z0Grid?: Float32Array): TerrainStatic {
  const { data: source, width: w, height: h, cellSize: cs } = grid;
  const n = w * h;

  /**
   * Flatten the sea before anything else looks at the elevations.
   *
   * Global DEMs (Terrarium among them) encode bathymetry, so the ocean arrives
   * as negative elevations with real relief — ridges, trenches, seamounts. Every
   * downstream quantity is a derivative of the height field, so left alone the
   * model computes slope, curvature, roughness and lee separation from the
   * shape of the sea floor and paints turbulence over open water in the pattern
   * of the bathymetry. Clamping at zero makes the sea what it actually is to the
   * air: flat.
   *
   * Only *sea* level is detectable this way. An alpine lake still reads as
   * terrain — harmless for the flow, since a lake surface is flat anyway, but
   * its z0 will be a land value rather than the water one.
   */
  const data = new Float32Array(n);
  const water = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const z = source[i];
    if (z > 0) {
      data[i] = z;
    } else {
      data[i] = 0;
      water[i] = 1;
    }
  }

  const dhdx = new Float32Array(n);
  const dhdy = new Float32Array(n);
  const slope = new Float32Array(n);
  const profileCurvature = new Float32Array(n);
  const planCurvature = new Float32Array(n);

  let minH = Infinity;
  let maxH = -Infinity;
  let sumH = 0;

  const at = (x: number, y: number) => data[clampIdx(y, h - 1) * w + clampIdx(x, w - 1)];

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const z = data[i];
      if (z < minH) minH = z;
      if (z > maxH) maxH = z;
      sumH += z;

      const zL = at(x - 1, y);
      const zR = at(x + 1, y);
      const zD = at(x, y - 1);
      const zU = at(x, y + 1);
      const zDL = at(x - 1, y - 1);
      const zDR = at(x + 1, y - 1);
      const zUL = at(x - 1, y + 1);
      const zUR = at(x + 1, y + 1);

      const p = (zR - zL) / (2 * cs); // ∂h/∂x
      const q = (zU - zD) / (2 * cs); // ∂h/∂y
      const r = (zR - 2 * z + zL) / (cs * cs); // ∂²h/∂x²
      const t = (zU - 2 * z + zD) / (cs * cs); // ∂²h/∂y²
      const s = (zUR - zUL - zDR + zDL) / (4 * cs * cs); // ∂²h/∂x∂y

      dhdx[i] = p;
      dhdy[i] = q;
      const g2 = p * p + q * q;
      slope[i] = Math.atan(Math.sqrt(g2));

      if (g2 < 1e-12) {
        // Flat ground: the steepest-descent direction is undefined, so both
        // reduce to mean curvature. Sign matches the sloped case above.
        profileCurvature[i] = -(r + t) / 2;
        planCurvature[i] = -(r + t) / 2;
      } else {
        const denom = Math.pow(1 + g2, 1.5);
        // Curvature along the steepest-descent line. Positive = convex = crest.
        profileCurvature[i] = -(r * p * p + 2 * s * p * q + t * q * q) / (g2 * denom);
        // Curvature across it. Positive = diverging contours = spur/nose.
        planCurvature[i] = -(r * q * q - 2 * s * p * q + t * p * p) / (g2 * Math.sqrt(1 + g2));
      }
    }
  }

  const radius = Math.max(2, Math.round(120 / cs));
  const roughness = detrendedRoughness(data, w, h, cs, radius, dhdx, dhdy);

  let z0: Float32Array;
  if (z0Grid && z0Grid.length === n) {
    z0 = z0Grid;
  } else {
    // No land-cover data: infer z0 from the detrended sub-grid roughness.
    //
    // z0 describes drag from features the grid does *not* resolve — vegetation,
    // boulders, buildings. Relief the grid does resolve is already in the flow
    // solution, so folding it in here would count it twice. Hence the
    // detrending above.
    //
    // The floor is 0.03 m (grass) and the cap 0.6 m (dense forest): without a
    // land-cover input this is an estimate, and claiming city-centre roughness
    // over a mountainside is not a defensible one. SPEC §3.2's proper answer is
    // an ESA WorldCover lookup, which is not wired up.
    z0 = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      // Open water is smooth by three orders of magnitude; the step at the
      // coastline is real and should stay sharp.
      z0[i] = water[i] > 0.5
        ? SEA_Z0
        : Math.min(0.6, Math.max(0.03, 0.03 + roughness[i] * 0.02));
    }
  }

  // Whatever texture the bathymetry had, the sea surface has none.
  for (let i = 0; i < n; i++) if (water[i] > 0.5) roughness[i] = 0;

  return {
    width: w,
    height: h,
    cellSize: cs,
    heights: data,
    water,
    dhdx,
    dhdy,
    slope,
    profileCurvature,
    planCurvature,
    roughness,
    z0,
    minH,
    maxH,
    meanH: sumH / n,
  };
}

/**
 * Sub-grid surface roughness: RMS deviation from the local *plane*, not the
 * local mean.
 *
 * This distinction is the whole point. A perfectly smooth 30° slope has large
 * RMS deviation about its window mean purely because it is tilted — the height
 * changes across the window — even though the surface has no texture at all.
 * Using that directly makes every mountainside look as rough as a city centre,
 * which inflates z0, raises the turbulence floor everywhere and over-damps the
 * near-surface wind.
 *
 * The tilt contribution is removable analytically. Over a square window of side
 * L, a uniform gradient ∇h produces variance
 *
 *     σ²_plane = |∇h|² · L² / 12
 *
 * (each axis contributes the variance of a uniform ramp, (gL)²/12). Subtracting
 * it leaves the variance the terrain has *in excess of* a plane, which is the
 * quantity z0 is supposed to describe. Clamped at zero, since on real DEMs the
 * local gradient estimate and the window statistics disagree slightly.
 */
export function detrendedRoughness(
  data: Float32Array,
  w: number,
  h: number,
  cellSize: number,
  radius: number,
  dhdx: Float32Array,
  dhdy: Float32Array
): Float32Array {
  const mean = boxBlur(data, w, h, radius);
  const sq = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) sq[i] = data[i] * data[i];
  const meanSq = boxBlur(sq, w, h, radius);

  const side = (2 * radius + 1) * cellSize;
  const planeFactor = (side * side) / 12;

  const out = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) {
    const variance = meanSq[i] - mean[i] * mean[i];
    const plane = (dhdx[i] * dhdx[i] + dhdy[i] * dhdy[i]) * planeFactor;
    out[i] = Math.sqrt(Math.max(0, variance - plane));
  }
  return out;
}

/**
 * RMS deviation from the local mean in a square window. Two-pass separable
 * box filter for the mean and the mean of squares, so cost is O(n) in the
 * window radius rather than O(n·r²).
 *
 * Note this includes the contribution of any overall tilt; for surface
 * roughness use `detrendedRoughness` instead.
 */
export function rmsRoughness(
  data: Float32Array,
  w: number,
  h: number,
  radius: number
): Float32Array {
  const mean = boxBlur(data, w, h, radius);
  const sq = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) sq[i] = data[i] * data[i];
  const meanSq = boxBlur(sq, w, h, radius);
  const out = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) {
    out[i] = Math.sqrt(Math.max(0, meanSq[i] - mean[i] * mean[i]));
  }
  return out;
}

/** Separable box blur with clamped edges. Used for roughness and for smoothing masks. */
export function boxBlur(src: Float32Array, w: number, h: number, radius: number): Float32Array {
  if (radius < 1) return src.slice();
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  const norm = 1 / (2 * radius + 1);

  for (let y = 0; y < h; y++) {
    const row = y * w;
    let acc = 0;
    for (let k = -radius; k <= radius; k++) acc += src[row + clampIdx(k, w - 1)];
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc * norm;
      acc += src[row + clampIdx(x + radius + 1, w - 1)] - src[row + clampIdx(x - radius, w - 1)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let k = -radius; k <= radius; k++) acc += tmp[clampIdx(k, h - 1) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc * norm;
      acc +=
        tmp[clampIdx(y + radius + 1, h - 1) * w + x] - tmp[clampIdx(y - radius, h - 1) * w + x];
    }
  }
  return out;
}

/**
 * Wind-relative terrain masks. `dirX, dirY` is the unit vector the air travels
 * towards (math convention, already converted from meteorological).
 *
 * `obstacleHeight` walks upwind from each cell to find the height of the last
 * significant crest the air crossed — this is the `h` that goes into the
 * Froude number and the separation length, and using a *local* obstacle height
 * rather than the whole-domain relief is what makes the rotor zones land in
 * roughly the right place on multi-ridge terrain.
 */
export function windMasks(
  terrain: TerrainStatic,
  heights: Float32Array,
  dirX: number,
  dirY: number
): TerrainWindMasks {
  const { width: w, height: h, cellSize: cs } = terrain;
  const n = w * h;
  const crest = new Float32Array(n);
  const fetch = new Float32Array(n);
  const obstacleHeight = new Float32Array(n);
  const crestDistance = new Float32Array(n);
  const crosswindExtent = new Float32Array(n);
  const leeSlope = new Float32Array(n);

  // Slope resolved along the wind axis. Positive = the ground falls away
  // downwind, which is the condition separation cares about.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const alongSlope = terrain.dhdx[i] * dirX + terrain.dhdy[i] * dirY;
      leeSlope[i] = Math.atan(-alongSlope);
    }
  }

  // Crest detection: convex along the wind axis, with real slope either side.
  // Evaluated on a wind-axis-smoothed profile so single-pixel DEM noise does
  // not register as a ridge.
  const smooth = boxBlur(heights, w, h, 1);
  const stepX = dirX;
  const stepY = dirY;
  const sampleAt = (fx: number, fy: number): number => {
    const x = Math.min(w - 1, Math.max(0, fx));
    const y = Math.min(h - 1, Math.max(0, fy));
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const x1 = Math.min(w - 1, x0 + 1);
    const y1 = Math.min(h - 1, y0 + 1);
    const tx = x - x0;
    const ty = y - y0;
    return (
      smooth[y0 * w + x0] * (1 - tx) * (1 - ty) +
      smooth[y0 * w + x1] * tx * (1 - ty) +
      smooth[y1 * w + x0] * (1 - tx) * ty +
      smooth[y1 * w + x1] * tx * ty
    );
  };

  const probe = Math.max(1, Math.round(80 / cs)); // ~80 m either side along the wind
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const back = sampleAt(x - stepX * probe, y - stepY * probe);
      const here = smooth[i];
      const fwd = sampleAt(x + stepX * probe, y + stepY * probe);
      const curv = (back - 2 * here + fwd) / (probe * cs * (probe * cs));
      // Convex along wind, and at least a modest gradient change.
      crest[i] = curv < -2e-4 && here > back - 5 ? 1 : 0;
    }
  }

  const radiusForMasks = Math.max(1, Math.round(150 / cs));

  // Upwind march: fetch, and the height of the highest point crossed within a
  // limited memory window (air forgets a ridge once it is far downwind).
  const maxMarch = Math.min(Math.max(w, h), Math.round(6000 / cs));
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const z0h = heights[i];
      let peak = z0h;
      let peakDist = 0;
      let dist = 0;
      let travelled = 0;
      for (let s = 1; s <= maxMarch; s++) {
        const px = x - stepX * s;
        const py = y - stepY * s;
        if (px < 0 || py < 0 || px > w - 1 || py > h - 1) break;
        travelled = s * cs;
        const zp = sampleAt(px, py);
        if (zp > peak) {
          peak = zp;
          peakDist = travelled;
        }
        dist = travelled;
      }
      fetch[i] = dist;
      // Obstacle height is the drop from the upwind crest to here, which is
      // what sets both the lee separation length and the Froude number.
      obstacleHeight[i] = peakDist === 0 ? 0 : Math.max(0, peak - z0h);
      crestDistance[i] = peakDist;
    }
  }

  /**
   * How far the shadowing obstacle extends across the wind.
   *
   * A long ridge forces air *over* it and leaves a persistent two-dimensional
   * wake. An isolated peak lets air go *around* the sides, which refills the
   * lee from both flanks and cuts the wake dramatically. Nothing in the model
   * distinguished the two, so a lone summit was given the same wake as an
   * unbroken escarpment.
   *
   * Measured by marching perpendicular to the wind from each cell and counting
   * how far the upwind obstacle height stays at least half of what it is here —
   * i.e. how wide the thing casting this shadow actually is.
   */
  const blurredObstacle = boxBlur(obstacleHeight, w, h, radiusForMasks);
  const perpX = -dirY;
  const perpY = dirX;
  const maxPerp = Math.min(Math.max(w, h), Math.round(4000 / cs));
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const c = j * w + i;
      const obs = blurredObstacle[c];
      if (obs < 30) continue;
      const half = obs * 0.5;
      let span = 0;
      for (const sign of [1, -1]) {
        for (let step = 1; step <= maxPerp; step++) {
          const x = Math.round(i + perpX * sign * step);
          const y = Math.round(j + perpY * sign * step);
          if (x < 0 || y < 0 || x >= w || y >= h) break;
          if (blurredObstacle[y * w + x] < half) break;
          span = step;
        }
        crosswindExtent[c] += span * cs;
        span = 0;
      }
    }
  }

  // The upwind march takes a running maximum, so the argmax flips between
  // competing peaks from one cell to the next and `obstacleHeight` steps by
  // tens of metres across a single cell. Everything downstream scales wake
  // length and rotor depth by it, so those steps show up as terrain-shaped
  // banding in the output. A short blur removes them without moving the ridges.
  return {
    crest,
    fetch,
    obstacleHeight: blurredObstacle,
    crestDistance: boxBlur(crestDistance, w, h, radiusForMasks),
    crosswindExtent,
    leeSlope,
  };
}

/**
 * Meteorological degrees (the direction wind comes *from*) to a math-convention
 * unit vector pointing where the air travels. This is the only place the
 * conversion should happen.
 */
export function metDegreesToVector(deg: number): [number, number] {
  const rad = ((deg + 180) * Math.PI) / 180;
  // Meteorological 0° = from north; after the +180 flip, 0 = towards north.
  return [Math.sin(rad), Math.cos(rad)];
}

/** Math-convention radians CCW from east, for the solver input. */
export function metDegreesToMathRad(deg: number): number {
  const [x, y] = metDegreesToVector(deg);
  return Math.atan2(y, x);
}

export function mathRadToMetDegrees(rad: number): number {
  const x = Math.cos(rad);
  const y = Math.sin(rad);
  const towards = (Math.atan2(x, y) * 180) / Math.PI;
  return ((towards + 180) % 360 + 360) % 360;
}
